import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Order } from '../../../store/order/api/interface';
import { CardMessage as CardMessageService } from '../../../client/services';
import { toastHandler } from '../../../utils/toastHandler';
import { parseCardMessage } from '../../../utils/cardMessageUtils';

interface CardMessageEditDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order | null | undefined;
  onCardMessageUpdate: (updatedOrder: Order) => void;
}

interface CardMessageData {
  toUser: string;
  message: string;
  fromUser: string;
}

const CardMessageEditDialog: React.FC<CardMessageEditDialogProps> = ({
  open,
  onClose,
  order,
  onCardMessageUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CardMessageData>({
    toUser: '',
    message: '',
    fromUser: '',
  });

  // Initialize form data when dialog opens
  useEffect(() => {
    if (open && order) {
      const cardMessage = parseCardMessage(order.cardMessage);
      
      setFormData({
        toUser: cardMessage.toUser || '',
        message: cardMessage.message || '',
        fromUser: cardMessage.fromUser || '',
      });
      setError(null);
    }
  }, [open, order]);

  const handleInputChange = (field: keyof CardMessageData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.toUser.trim()) {
      setError('To field is required');
      return false;
    }
    if (!formData.message.trim()) {
      setError('Message is required');
      return false;
    }
    if (!formData.fromUser.trim()) {
      setError('From field is required');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSave = async () => {
    if (!validateForm() || !order) return;

    setLoading(true);
    setError(null);

    try {
      // Prepare card message data in the required format
      const cardMessage = {
        cardMessage: {
          id: null,
          toUser: formData.toUser.trim(),
          userId: null,
          message: formData.message.trim(),
          fromUser: formData.fromUser.trim(),
        }
      };

      console.log('Updating card message via new API:', cardMessage);

      // Use the new dedicated card message API
      const response = await CardMessageService.patch(order.id, cardMessage);
      
      if (response && response.success) {
        // Update the order with new data
        const updatedOrder = {
          ...order,
          cardMessage: response.cardMessage,
        };
        
        onCardMessageUpdate(updatedOrder);
        onClose();
        
        // Show success message
        toastHandler({
          message: response.message || 'Card message updated successfully',
          type: 'success',
        });
      }
    } catch (error: any) {
      console.error('Error updating card message:', error);
      setError(error.message || 'Failed to update card message');
      toastHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Edit Card Message</Typography>
        <Typography variant="body2" color="text.secondary">
          Update the gift card message for this order
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Edit the gift card message details for this order.
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="To"
                value={formData.toUser}
                onChange={(e) => handleInputChange('toUser', e.target.value)}
                required
                disabled={loading}
                placeholder="Recipient's name"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message"
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                required
                disabled={loading}
                multiline
                rows={4}
                placeholder="Your gift message"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="From"
                value={formData.fromUser}
                onChange={(e) => handleInputChange('fromUser', e.target.value)}
                required
                disabled={loading}
                placeholder="Sender's name"
              />
            </Grid>
          </Grid>

          {/* Preview Section */}
          {(formData.toUser || formData.message || formData.fromUser) && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Preview:
              </Typography>
              <Box sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1, bgcolor: 'white' }}>
                {formData.toUser && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>To:</strong> {formData.toUser}
                  </Typography>
                )}
                {formData.message && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Message:</strong> {formData.message}
                  </Typography>
                )}
                {formData.fromUser && (
                  <Typography variant="body2">
                    <strong>From:</strong> {formData.fromUser}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : 'Save Message'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CardMessageEditDialog;

